import React, { useState, useEffect } from 'react';
import { Eye, CheckCircle, XCircle, Clock, Download, User, HardDrive } from 'lucide-react';
import toast from 'react-hot-toast';
import { API_URL } from '../config';

const AdminActivationKeys = () => {
  const [activationKeys, setActivationKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [approvalForm, setApprovalForm] = useState({
    licenseType: 'STANDARD',
    validityDays: 365,
    employeeId: '',
    salesRegion: '',
    responseNotes: ''
  });

  // Set page title
  useEffect(() => {
    document.title = 'Activation Keys | Admin Portal';
  }, []);

  useEffect(() => {
    fetchActivationKeys();
  }, [filterStatus]);

  const fetchActivationKeys = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = filterStatus === 'ALL' 
        ? `${API_URL}/activation-keys`
        : `${API_URL}/activation-keys?status=${filterStatus}`;
        
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setActivationKeys(data);
      }
    } catch (error) {
      console.error('Failed to fetch activation keys:', error);
    } finally {
      setLoading(false);
    }
  };

  const viewDetails = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/activation-keys/${id}/details`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedKey(data);
        setShowDetailsModal(true);
      }
    } catch (error) {
      console.error('Failed to fetch details:', error);
      toast.error('Failed to load activation key details');
    }
  };

  const openApprovalModal = (key) => {
    setSelectedKey(key);
    setApprovalForm({
      licenseType: 'STANDARD',
      validityDays: 365,
      employeeId: '',
      salesRegion: '',
      responseNotes: ''
    });
    setShowApprovalModal(true);
  };

  const approveKey = async () => {
    // Validate required fields
    if (!approvalForm.licenseType) {
      toast.error('License type is required');
      return;
    }
    if (!approvalForm.validityDays || approvalForm.validityDays < 1) {
      toast.error('Validity days must be at least 1');
      return;
    }

    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/activation-keys/${selectedKey.id}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          licenseType: approvalForm.licenseType,
          validityDays: parseInt(approvalForm.validityDays, 10),
          employeeId: approvalForm.employeeId || null,
          salesRegion: approvalForm.salesRegion || null,
          responseNotes: approvalForm.responseNotes || null,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Activation key approved successfully! The customer will receive an email with the signed file.');
        setShowApprovalModal(false);
        setShowDetailsModal(false);
        setSelectedKey(null);
        fetchActivationKeys();
      } else {
        toast.error(data.error || 'Failed to approve activation key');
      }
    } catch (error) {
      console.error('Approve error:', error);
      toast.error('Failed to approve activation key');
    } finally {
      setProcessing(false);
    }
  };

  const rejectKey = async (id) => {
    const responseNotes = prompt('Enter rejection reason:');
    if (!responseNotes) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/activation-keys/${id}/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ responseNotes }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Activation key rejected. The customer will be notified.');
        setShowDetailsModal(false);
        setSelectedKey(null);
        fetchActivationKeys();
      } else {
        alert(data.error || 'Failed to reject activation key');
      }
    } catch (error) {
      console.error('Reject error:', error);
      toast.error('Failed to reject activation key');
    } finally {
      setProcessing(false);
    }
  };

  const downloadSignedKey = async (id, filename) => {
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/activation-keys/download/${id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const data = await response.json();
        toast.error(data.error || 'Failed to download signed activation key');
        return;
      }

      // Get the blob from response
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || `activation-key-${id}.xlic`;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Signed activation key downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download signed activation key');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: {
        icon: Clock,
        className: 'bg-yellow-100 text-yellow-800',
        label: 'Pending',
      },
      APPROVED: {
        icon: CheckCircle,
        className: 'bg-green-100 text-green-800',
        label: 'Approved',
      },
      REJECTED: {
        icon: XCircle,
        className: 'bg-red-100 text-red-800',
        label: 'Rejected',
      },
    };

    const config = statusConfig[status] || statusConfig.PENDING;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const pendingCount = activationKeys.filter(k => k.status === 'PENDING').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 text-sm">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Activation Keys (XDeploy Deployment Tool)</h1>
          <p className="text-sm text-gray-600 mt-1">Manage activation licenses for XDeploy deployment tool access</p>
          <p className="mt-1 text-sm text-gray-500">
            Review and approve customer requests
          </p>
        </div>
        {pendingCount > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
            <span className="text-amber-800 font-medium text-sm">{pendingCount} pending</span>
          </div>
        )}
      </div>

      {/* Filter Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2">
        <div className="flex space-x-2">
          {['ALL', 'PENDING', 'APPROVED', 'REJECTED'].map((status) => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              style={filterStatus === status ? { backgroundColor: '#197560' } : {}}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                filterStatus === status
                  ? 'text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {/* Activation Keys Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {activationKeys.length === 0 ? (
          <div className="p-6 text-center text-gray-500 text-sm">
            <HardDrive className="mx-auto h-10 w-10 text-gray-400 mb-2" />
            <p>No activation keys found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">
                    Customer
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">
                    Hostname
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">
                    Hardware
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">
                    Submitted
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {activationKeys.map((key) => (
                  <tr key={key.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <User className="w-4 h-4 text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {key.user?.firstName || 'N/A'} {key.user?.lastName || ''}
                          </div>
                          <div className="text-xs text-gray-500">{key.user?.email || 'N/A'}</div>
                          {key.user?.company && (
                            <div className="text-xs text-gray-400">{key.user.company}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {key.hostname || 'Unknown'}
                      </div>
                      <div className="text-xs text-gray-500">{key.originalFilename}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {key.cpuSockets && key.cpuCores ? (
                          <>
                            <div>{key.cpuSockets} sockets</div>
                            <div className="text-xs text-gray-500">{key.cpuCores} cores</div>
                          </>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(key.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(key.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => viewDetails(key.id)}
                        className="inline-flex items-center text-blue-600 hover:text-blue-900"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </button>
                      {key.status === 'APPROVED' && key.signedFilePath && (
                        <button
                          onClick={() => downloadSignedKey(key.id, key.signedFilename || `activation-${key.hostname}.xlic`)}
                          className="inline-flex items-center text-purple-600 hover:text-purple-900"
                          disabled={processing}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Download
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedKey && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto m-4">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Activation Key Details</h2>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Customer Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Customer Information</h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-gray-500">Name:</span>
                      <p className="font-medium">
                        {selectedKey.user?.firstName || 'N/A'} {selectedKey.user?.lastName || ''}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Email:</span>
                      <p className="font-medium">{selectedKey.user?.email || 'N/A'}</p>
                    </div>
                    {selectedKey.user?.company && (
                      <div>
                        <span className="text-sm text-gray-500">Company:</span>
                        <p className="font-medium">{selectedKey.user.company}</p>
                      </div>
                    )}
                    {selectedKey.user?.phone && (
                      <div>
                        <span className="text-sm text-gray-500">Phone:</span>
                        <p className="font-medium">{selectedKey.user.phone}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Decrypted Hardware Information */}
              {selectedKey.decryptedData && (() => {
                // Extract hardware info from nested host object
                const hostData = selectedKey.decryptedData.host || {};
                const hostKey = Object.keys(hostData)[0];
                const hardware = hostKey ? hostData[hostKey] : {};
                
                return (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">
                      Hardware Information (Decrypted)
                    </h3>
                    <div className="bg-blue-50 rounded-lg p-4 space-y-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <span className="text-sm text-gray-600">Hostname:</span>
                          <p className="font-medium text-gray-900">
                            {hostKey || selectedKey.hostname || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">Product UUID:</span>
                          <p className="font-mono text-xs text-gray-900">
                            {hardware.product_uuid || selectedKey.productUuid || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">Machine ID:</span>
                          <p className="font-mono text-xs text-gray-900 break-all">
                            {hardware.machine_id || selectedKey.machineId || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">CPU Configuration:</span>
                          <p className="font-medium text-gray-900">
                            {hardware.cpu_sockets || selectedKey.cpuSockets || 0} sockets, {hardware.cpu_cores || selectedKey.cpuCores || 0} cores
                          </p>
                        </div>
                      </div>

                      {/* Full Decrypted Data */}
                      <details className="mt-4">
                        <summary className="text-sm text-gray-600 cursor-pointer hover:text-gray-900">
                          View Full Data (JSON)
                        </summary>
                        <pre className="mt-2 bg-gray-900 text-green-400 p-3 rounded text-xs overflow-x-auto">
                          {JSON.stringify(selectedKey.decryptedData, null, 2)}
                        </pre>
                      </details>
                    </div>
                  </div>
                );
              })()}

              {/* Request Details */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Request Details</h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-gray-500">Status:</span>
                      <div className="mt-1">{getStatusBadge(selectedKey.status)}</div>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Submitted:</span>
                      <p className="font-medium">{formatDate(selectedKey.createdAt)}</p>
                    </div>
                    {selectedKey.approvedAt && (
                      <div>
                        <span className="text-sm text-gray-500">Approved:</span>
                        <p className="font-medium">{formatDate(selectedKey.approvedAt)}</p>
                      </div>
                    )}
                  </div>

                  {selectedKey.notes && (
                    <div className="pt-2">
                      <span className="text-sm text-gray-500">Customer Notes:</span>
                      <p className="mt-1 text-gray-900">{selectedKey.notes}</p>
                    </div>
                  )}

                  {selectedKey.responseNotes && (
                    <div className="pt-2">
                      <span className="text-sm text-gray-500">Admin Response:</span>
                      <p className="mt-1 text-gray-900">{selectedKey.responseNotes}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              {selectedKey.status === 'PENDING' && (
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => rejectKey(selectedKey.id)}
                    disabled={processing}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    {processing ? 'Processing...' : 'Reject'}
                  </button>
                  <button
                    onClick={() => {
                      setShowDetailsModal(false);
                      openApprovalModal(selectedKey);
                    }}
                    disabled={processing}
                    style={{ backgroundColor: '#197560' }}
                    className="px-4 py-2 text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    {processing ? 'Processing...' : 'Approve & Sign'}
                  </button>
                </div>
              )}
              {selectedKey.status === 'APPROVED' && selectedKey.signedFilePath && (
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => downloadSignedKey(selectedKey.id, `activation-${selectedKey.hostname}-signed.xlic`)}
                    disabled={processing}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {processing ? 'Downloading...' : 'Download Signed Key'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Approval Modal */}
      {showApprovalModal && selectedKey && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Approve Activation Key</h2>
                <button
                  onClick={() => setShowApprovalModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              {/* Customer Info */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Customer Information</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">Name:</span>
                    <span className="ml-2 font-medium">{selectedKey.user?.firstName || 'N/A'} {selectedKey.user?.lastName || ''}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Email:</span>
                    <span className="ml-2 font-medium">{selectedKey.user?.email || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Hostname:</span>
                    <span className="ml-2 font-medium">
                      {(() => {
                        const hostData = selectedKey.decryptedData?.host || {};
                        const hostKey = Object.keys(hostData)[0];
                        return hostKey || selectedKey.hostname || 'N/A';
                      })()}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Hardware:</span>
                    <span className="ml-2 font-medium">
                      {(() => {
                        const hostData = selectedKey.decryptedData?.host || {};
                        const hostKey = Object.keys(hostData)[0];
                        const hardware = hostKey ? hostData[hostKey] : {};
                        const sockets = hardware.cpu_sockets || selectedKey.cpuSockets || 0;
                        const cores = hardware.cpu_cores || selectedKey.cpuCores || 0;
                        return `${sockets} sockets, ${cores} cores`;
                      })()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Approval Form */}
              <div className="space-y-4">
                {/* License Type - Required */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    License Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={approvalForm.licenseType}
                    onChange={(e) => setApprovalForm({ ...approvalForm, licenseType: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="STANDARD">Standard</option>
                    <option value="PROFESSIONAL">Professional</option>
                    <option value="ENTERPRISE">Enterprise</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">Select the license tier for this customer</p>
                </div>

                {/* Validity Days - Required */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Validity (Days) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={approvalForm.validityDays}
                    onChange={(e) => setApprovalForm({ ...approvalForm, validityDays: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">Number of days until license expires (default: 365)</p>
                </div>

                {/* Employee ID - Optional */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Employee ID
                  </label>
                  <input
                    type="text"
                    value={approvalForm.employeeId}
                    onChange={(e) => setApprovalForm({ ...approvalForm, employeeId: e.target.value })}
                    placeholder="e.g., EMP-12345"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">Sales employee ID (optional)</p>
                </div>

                {/* Sales Region - Optional */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sales Region
                  </label>
                  <select
                    value={approvalForm.salesRegion}
                    onChange={(e) => setApprovalForm({ ...approvalForm, salesRegion: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">-- Select Region --</option>
                    <option value="North America">North America</option>
                    <option value="South America">South America</option>
                    <option value="Europe">Europe</option>
                    <option value="Asia Pacific">Asia Pacific</option>
                    <option value="Middle East">Middle East</option>
                    <option value="Africa">Africa</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">Geographic sales region (optional)</p>
                </div>

                {/* Response Notes - Optional */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Approval Notes
                  </label>
                  <textarea
                    value={approvalForm.responseNotes}
                    onChange={(e) => setApprovalForm({ ...approvalForm, responseNotes: e.target.value })}
                    placeholder="Enter any notes or comments for the customer..."
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">Additional notes to include with the license (optional)</p>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowApprovalModal(false)}
                  disabled={processing}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={approveKey}
                  disabled={processing}
                  className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center"
                >
                  {processing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve & Sign
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminActivationKeys;
